// src/pages/Profile.js
// Account settings: profile (avatar, name, timezone), email, password, signed-in
// devices and account deletion.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { profileAPI } from '../utils/api';
import { useStore } from '../store';

const errorOf = (err, fallback) => err.response?.data?.error || fallback;

const TIMEZONES = (() => {
  try { return Intl.supportedValuesOf('timeZone'); } catch { return ['UTC']; }
})();

// "Chrome on macOS" from a user-agent string — good enough to recognise a device.
function describeDevice(ua = '') {
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : /curl\//.test(ua) ? 'curl' : 'Unknown browser';
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'macOS'
    : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

const fmtDate = (d) => new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function Section({ title, subtitle, children, danger }) {
  return (
    <div className="card" style={{ marginBottom: 16, borderColor: danger ? '#fecaca' : undefined }}>
      <div className="card-header" style={{ display: 'block' }}>
        <div className="card-title" style={{ color: danger ? '#b91c1c' : undefined }}>{title}</div>
        {subtitle && <div className="page-subtitle">{subtitle}</div>}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

export function Avatar({ user, size = 30 }) {
  const style = { width: size, height: size, fontSize: Math.round(size * 0.4) };
  return user?.avatar_url
    ? <img className="sb-avatar" src={user.avatar_url} alt="" style={{ ...style, objectFit: 'cover' }} />
    : <div className="sb-avatar" style={style}>{user?.name?.[0]?.toUpperCase() || 'U'}</div>;
}

function ProfileSection({ user, setUser }) {
  const [name, setName] = useState(user.name || '');
  const [timezone, setTimezone] = useState(user.timezone || 'UTC');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const tzOptions = useMemo(
    () => (TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES]),
    [timezone]
  );
  const dirty = name.trim() !== user.name || timezone !== user.timezone;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await profileAPI.update({ name: name.trim(), timezone });
      setUser(res.data.user);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(errorOf(err, 'Could not update profile'));
    } finally { setSaving(false); }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Avatar must be 2MB or smaller'); return; }
    setUploading(true);
    try {
      const res = await profileAPI.uploadAvatar(file);
      setUser(res.data.user);
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(errorOf(err, 'Upload failed'));
    } finally { setUploading(false); }
  };

  const removePhoto = async () => {
    try {
      const res = await profileAPI.removeAvatar();
      setUser(res.data.user);
      toast.success('Profile photo removed');
    } catch (err) {
      toast.error(errorOf(err, 'Could not remove photo'));
    }
  };

  return (
    <Section title="Profile" subtitle="How you appear in Post Calendar">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
        <Avatar user={user} size={64} />
        <div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-outline" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? 'Uploading…' : user.avatar_url ? 'Change photo' : 'Upload photo'}
            </button>
            {user.avatar_url && (
              <button type="button" className="btn btn-ghost" onClick={removePhoto}>Remove</button>
            )}
          </div>
          <div className="page-subtitle" style={{ marginTop: 6 }}>JPG, PNG, WebP or GIF, up to 2MB.</div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={onPick} />
        </div>
      </div>

      <form onSubmit={save}>
        <div className="form-group">
          <label className="form-label">Full name<span className="req">*</span></label>
          <input className="form-input" value={name} maxLength={255} onChange={e => setName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">Timezone</label>
          <select className="form-select" value={timezone} onChange={e => setTimezone(e.target.value)}>
            {tzOptions.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-primary" disabled={!dirty || saving || !name.trim()}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <span className="page-subtitle">
            <span style={{ textTransform: 'capitalize' }}>{user.plan || 'free'}</span> plan
            {user.created_at && ` · Member since ${new Date(user.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}`}
          </span>
        </div>
      </form>
    </Section>
  );
}

function EmailSection({ user, setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  // The new address only takes effect once its confirmation link is clicked.
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await profileAPI.changeEmail({ email: email.trim(), current_password: password });
      setUser({ ...user, pending_email: res.data.pending_email });
      setEmail(''); setPassword('');
      toast.success(`Confirmation link sent to ${res.data.pending_email}`);
    } catch (err) {
      toast.error(errorOf(err, 'Could not update email'));
    } finally { setSaving(false); }
  };

  const cancelPending = async () => {
    try {
      await profileAPI.cancelEmailChange();
      setUser({ ...user, pending_email: null });
      toast.success('Email change cancelled');
    } catch (err) {
      toast.error(errorOf(err, 'Could not cancel'));
    }
  };

  const subtitle = (
    <>
      You sign in with <strong>{user.email}</strong>{' '}
      {user.email_verified_at
        ? <span className="badge badge-delivered">Verified</span>
        : <span className="badge badge-pending_review">Not verified</span>}
    </>
  );

  return (
    <Section title="Email address" subtitle={subtitle}>
      {user.pending_email && (
        <div style={{ background: '#fff8e7', color: '#8a6200', borderRadius: 6, padding: '10px 12px', fontSize: 12, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>Waiting for you to confirm <strong>{user.pending_email}</strong> — check that inbox for the link.</span>
          <button type="button" className="btn btn-ghost" style={{ marginLeft: 'auto', padding: '3px 8px' }} onClick={cancelPending}>Cancel</button>
        </div>
      )}
      <form onSubmit={save}>
        <div className="form-group">
          <label className="form-label">New email</label>
          <input className="form-input" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">Current password</label>
          <input className="form-input" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required />
        </div>
        <button className="btn btn-primary" disabled={saving || !email.trim() || !password}>
          {saving ? 'Sending…' : 'Send confirmation link'}
        </button>
        <div className="page-subtitle" style={{ marginTop: 8 }}>
          Your email changes once you click the link we send to the new address.
        </div>
      </form>
    </Section>
  );
}

function PasswordSection({ onChanged }) {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const mismatch = form.confirm && form.next !== form.confirm;
  const tooShort = form.next && form.next.length < 8;

  const save = async (e) => {
    e.preventDefault();
    if (mismatch || tooShort) return;
    setSaving(true);
    try {
      const res = await profileAPI.changePassword({ current_password: form.current, new_password: form.next });
      setForm({ current: '', next: '', confirm: '' });
      const n = res.data.other_sessions_signed_out;
      toast.success(n ? `Password changed — signed out ${n} other device${n > 1 ? 's' : ''}` : 'Password changed');
      onChanged();
    } catch (err) {
      toast.error(errorOf(err, 'Could not change password'));
    } finally { setSaving(false); }
  };

  return (
    <Section title="Password" subtitle="Changing it signs you out on every other device">
      <form onSubmit={save}>
        <div className="form-group">
          <label className="form-label">Current password</label>
          <input className="form-input" type="password" autoComplete="current-password" value={form.current} onChange={set('current')} required />
        </div>
        <div className="form-group">
          <label className="form-label">New password</label>
          <input className="form-input" type="password" autoComplete="new-password" maxLength={72} value={form.next} onChange={set('next')} placeholder="Min 8 characters" required />
          {tooShort && <div className="form-error">Use at least 8 characters</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Confirm new password</label>
          <input className="form-input" type="password" autoComplete="new-password" maxLength={72} value={form.confirm} onChange={set('confirm')} required />
          {mismatch && <div className="form-error">Passwords do not match</div>}
        </div>
        <button className="btn btn-primary" disabled={saving || !form.current || !form.next || mismatch || tooShort}>
          {saving ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </Section>
  );
}

function SessionsSection({ reloadKey }) {
  const [sessions, setSessions] = useState(null);

  const load = () => profileAPI.sessions()
    .then(r => setSessions(r.data.sessions))
    .catch(() => setSessions([]));

  useEffect(() => { load(); }, [reloadKey]);

  const revoke = async (id) => {
    try {
      await profileAPI.revokeSession(id);
      setSessions(s => s.filter(x => x.id !== id));
      toast.success('Device signed out');
    } catch (err) {
      toast.error(errorOf(err, 'Could not sign out that device'));
    }
  };

  return (
    <Section title="Signed-in devices" subtitle="Sign out anywhere you don't recognise">
      {!sessions ? <div className="spinner" /> : (
        <table className="data-table">
          <tbody>
            {sessions.map(s => (
              <tr key={s.id}>
                <td style={{ width: 'auto' }}>
                  <div style={{ fontWeight: 600 }}>
                    {describeDevice(s.user_agent)}
                    {s.current && <span className="badge badge-delivered" style={{ marginLeft: 8 }}>This device</span>}
                  </div>
                  <div className="page-subtitle">Signed in {fmtDate(s.created_at)}{s.ip ? ` · ${s.ip.replace(/^::ffff:/, '')}` : ''}</div>
                </td>
                <td style={{ width: 1, whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {!s.current && <button className="btn btn-outline" onClick={() => revoke(s.id)}>Sign out</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function DeleteAccountSection({ user }) {
  const isOwner = user.org_role === 'owner';
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const setUser = useStore(s => s.setUser);

  const remove = async () => {
    setDeleting(true);
    try {
      await profileAPI.deleteAccount(password);
      setUser(null);
      toast.success('Your account has been deleted');
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(errorOf(err, 'Could not delete account'));
      setDeleting(false);
    }
  };

  return (
    <Section title="Delete account" danger
      subtitle={isOwner
        ? `You own ${user.organization_name}. Deleting your account also deletes the organization's posts, connected accounts, groups and media. This cannot be undone.`
        : `Removes your sign-in. Posts, accounts and groups you created stay with ${user.organization_name}.`}>
      <button className="btn btn-danger" onClick={() => setOpen(true)}>Delete my account</button>
      {open && (
        <div className="modal-overlay" onClick={() => !deleting && setOpen(false)}>
          <div className="modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Delete account?</div></div>
            <div className="modal-body">
              <p style={{ fontSize: 12, color: '#4a5568', marginBottom: 14 }}>
                {isOwner
                  ? "Everything in your organization will be deleted immediately. Scheduled posts will not be published. If others are on your team, transfer ownership on the Team page first."
                  : 'Your sign-in will be deleted immediately.'}
              </p>
              <div className="form-group">
                <label className="form-label">Current password</label>
                <input className="form-input" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Type DELETE to confirm</label>
                <input className="form-input" value={confirmText} onChange={e => setConfirmText(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" disabled={deleting} onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-danger" disabled={deleting || !password || confirmText !== 'DELETE'} onClick={remove}>
                {deleting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

export default function Profile() {
  const { user, setUser } = useStore();
  const [sessionsKey, setSessionsKey] = useState(0);

  // Refresh from the server so the page never edits a stale copy.
  useEffect(() => { profileAPI.get().then(r => setUser(r.data.user)).catch(() => {}); }, [setUser]);

  if (!user) return null;
  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Profile settings</div>
          <div className="page-subtitle">Manage your personal details, sign-in and security</div>
        </div>
      </div>
      <div style={{ padding: 16, maxWidth: 640 }}>
        <ProfileSection key={`${user.name}|${user.timezone}`} user={user} setUser={setUser} />
        {user.role === 'superadmin' ? (
          <>
            <Section title="Superadmin sign-in" subtitle="Managed in the server configuration">
              <p style={{ fontSize: 12, color: '#4a5568', lineHeight: 1.6 }}>
                You sign in as <strong>{user.email}</strong>. This account's email and password come from
                {' '}<code>SUPERADMIN_EMAIL</code> and <code>SUPERADMIN_PASSWORD</code> in the server's
                {' '}<code>backend/.env</code> file — change them there and restart the app.
              </p>
            </Section>
            <SessionsSection reloadKey={sessionsKey} />
          </>
        ) : (
          <>
            <EmailSection user={user} setUser={setUser} />
            <PasswordSection onChanged={() => setSessionsKey(k => k + 1)} />
            <SessionsSection reloadKey={sessionsKey} />
            <DeleteAccountSection user={user} />
          </>
        )}
      </div>
    </>
  );
}
